import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  loadPartners,
  loadShareholders,
  saveShareholders,
  loadPartnerDetails,
  loadPartnerShareholdersSummary
} from "../../actionCreators";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ShareholderComponent from "./ShareholderComponent";
import { getShareholders, getShareholdersSummary } from "../../selectors";
import {
  shape,
  required,
  concatFailure,
  items,
  Validator,
  Failure,
  Success,
  ValidationError
} from "@totalsoft/pure-validations";
import { useTranslation } from "react-i18next";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";

const ShareholderContainer = ({
  partnerId,
  actions,
  shareholders,
  shareholdersLoaded,
  setIsDirty,
  shareholdersSummary,
  shareholdersSummaryLoaded,
  hasWritePermission,
  viewShareholderPhone
}) => {
  const { t } = useTranslation();

  const [localShareholders, setLocalShareholderList] = useState(shareholders || []);
  const [shareholderCardExpanded, setShareholderCardExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const realBeneficiaryThreshold = 25;
  const totalPercentageValidator = Validator(function totalPercentageValidator(content) {
    return shareholdersLoaded && istotalPercentageValid(content)(0, 100)
      ? Success
      : Failure(ValidationError(t("Shareholders.ValidateTotalPercent")));
  });

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const validatorShareholderValidator =
    [
      items(
        shape({
          uniqueID: required,
          sharePercent: required
        })
      ),
      totalPercentageValidator
    ] |> concatFailure;

  const [validationShareholderSelection, validateShareholderSelection] = useDirtyFieldValidation(
    validatorShareholderValidator,
    dirtyInfo
  );

  const saveShareholders = (partnerId, localShareholders) => {
    if (!hasWritePermission || !validateShareholderSelection(localShareholders)) {
      return;
    }
    actions.saveShareholders(partnerId, localShareholders, () => {
      setIsDirty(false);
    });
    setSaveDisabled(true);
  };

  const addShareholder = () => {
    let shareholderList = [...localShareholders, {}];
    setLocalShareholderList(shareholderList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const deleteShareholder = item => {
    let newShareholderList = localShareholders.filter(
      l => localShareholders.indexOf(l) !== localShareholders.indexOf(item)
    );
    setLocalShareholderList(newShareholderList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const onShareholderCardExpanded = expanded => {
    if (shareholderCardExpanded !== expanded) setShareholderCardExpanded(expanded);
  };

  useEffect(() => {
    if (shareholderCardExpanded && !shareholdersLoaded && partnerId !== null) {
      actions.loadShareholders(partnerId);
    }
  }, [shareholderCardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shareholdersLoaded) {
      setLocalShareholderList(shareholders);
    }
  }, [shareholdersLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validateShareholderSelection(localShareholders, dirtyInfo);
  }, [localShareholders, dirtyInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shareholdersSummaryLoaded && partnerId !== null) {
      actions.loadPartnerShareholdersSummary(partnerId);
    }
  }, [shareholdersSummaryLoaded, partnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPartners = search => {
    if (!search) {
      return Promise.resolve([]);
    }

    return actions.loadPartners(search).then(data => data.map(({ id, uniqueId: uniqueID }) => ({ id, uniqueID })));
  };

  const loadPartnerDetails = (uniqueID, idx) => {
    if (!uniqueID) {
      return Promise.resolve({ options: [] });
    }
    actions.loadPartnerDetails(uniqueID, true).then(data => {
      const list = Object.assign([], {
        ...localShareholders,
        [idx]: {
          ...localShareholders[idx],
          uniqueID: data.uniqueID,
          isPF: data.isPF,
          isRealBeneficiary: false,
          sharePercent: null,
          shareHolderPartnerName: data.partnerName,
          shareHolderPhoneNumber: data.phoneNumber,
          shareHolderPartnerId: data.partnerId,
          shareholders: data.shareholders
        }
      });
      setLocalShareholderList(list);
    });
  };

  const onShareholderPropertyChange =
    idx =>
    (prop1, prop2, callbackFn = () => {}) =>
    (value1, value2) => {
      setDirtyInfo(`${prop1}`);

      const isSharePercentProp = prop1 === "sharePercent";
      const exceedThreshold = value1 >= realBeneficiaryThreshold;

      setLocalShareholderList(
        Object.assign([], {
          ...localShareholders,
          [idx]: {
            ...localShareholders[idx],
            [prop1]: value1,
            ...(prop2 && { [prop2]: value2 }),
            ...(isSharePercentProp && { isRealBeneficiary: exceedThreshold && localShareholders[idx].isPF })
          }
        })
      );
      if (value2) {
        callbackFn(value2, idx);
      }
      setSaveDisabled(false);
      setIsDirty(true);
    };

  const istotalPercentageValid = localShareholders => (min, max) => {
    if (localShareholders === null) return true;
    if (localShareholders.length === 0) return true;

    const totalPercentage = localShareholders.reduce((totalPercentage, shareholder) => {
      return totalPercentage + Number(shareholder.sharePercent === undefined ? 0 : shareholder.sharePercent);
    }, 0);
    if (min <= totalPercentage && totalPercentage <= max) return true;
    return false;
  };

  const openPartnerWindow = partnerId => e => {
    e.stopPropagation();
    window.open(`/partners/${partnerId}`, "_blank");
  };

  return (
    <ShareholderComponent
      shareholderList={localShareholders}
      loadPartners={loadPartners}
      onPropertyChange={onShareholderPropertyChange}
      saveShareholders={saveShareholders}
      addShareholder={addShareholder}
      deleteShareholder={deleteShareholder}
      partnerId={partnerId}
      shareholderCardExpanded={shareholderCardExpanded}
      onShareholderCardExpanded={onShareholderCardExpanded}
      loadPartnerDetails={loadPartnerDetails}
      saveDisabled={saveDisabled}
      validationShareholderSelection={validationShareholderSelection}
      totalPercentageValidator={totalPercentageValidator}
      shareholdersSummary={shareholdersSummary}
      shareholdersSummaryLoaded={shareholdersSummaryLoaded}
      hasWritePermission={hasWritePermission}
      openPartnerWindow={openPartnerWindow}
      viewShareholderPhone={viewShareholderPhone}
    />
  );
};
ShareholderContainer.propTypes = {
  partnerId: PropTypes.number,
  actions: PropTypes.object.isRequired,
  shareholders: PropTypes.array,
  shareholdersLoaded: PropTypes.bool.isRequired,
  shareholdersLoading: PropTypes.bool.isRequired,
  setIsDirty: PropTypes.func,
  shareholdersSummary: PropTypes.array,
  shareholdersSummaryLoaded: PropTypes.bool,
  hasWritePermission: PropTypes.bool.isRequired,
  viewShareholderPhone: PropTypes.bool.isRequired
};

function mapStateToProps(state, ownProps) {
  const {
    content: shareholders,
    loaded: shareholdersLoaded,
    loading: shareholdersLoading
  } = getShareholders(state, ownProps.partnerId);
  const { shareholdersSummary, summaryLoaded } = getShareholdersSummary(state, ownProps.partnerId);

  return {
    shareholders,
    shareholdersLoaded,
    shareholdersLoading,
    shareholdersSummary,
    shareholdersSummaryLoaded: summaryLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadPartners,
        loadShareholders,
        saveShareholders,
        loadPartnerDetails,
        loadPartnerShareholdersSummary
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ShareholderContainer);
